import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Plus, Loader2, X, RotateCcw, ChevronLeft, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogBody,
} from '@/components/ui/dialog';
import { toast } from '@/components/ui/use-toast';
import {
  api,
  ContactDetail,
  ContactCategory,
  ContactPriority,
  ContactInput,
  ContactCategoryInput,
  ContactPriorityInput,
  LabeledValue,
  AddressValue,
  CustomFieldDefinition,
} from '@/lib/api';
import {
  EMAIL_LABELS,
  PHONE_LABELS,
  ADDRESS_LABELS,
  WEBSITE_LABELS,
  PRESET_COLORS,
  HistoryEntry,
  FormSnapshot,
} from '../types';
import type { DefaultTemplate } from '../types';
import { ContactAttachments } from './ContactAttachments';
import { LabeledEntryList } from './LabeledEntryList';
import { ContactAvatar } from './ContactCard';
import { CustomFieldInputs, ManageCustomFieldsDialog } from '../CustomFieldsDialog';
import { InteractionsTimeline } from '../InteractionsTimeline';
import { RelationshipsSection } from '../RelationshipsSection';

export function ContactFormDialog({
  open,
  onOpenChange,
  contact,
  categories,
  priorities,
  onSave,
  onCreateCategory,
  onCreatePriority,
  onToggleFavorite,
  defaultTemplate,
  suggestedTags = [],
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contact: ContactDetail | null;
  categories: ContactCategory[];
  priorities: ContactPriority[];
  onSave: (data: ContactInput, contactId?: string) => Promise<void>;
  onCreateCategory: (data: ContactCategoryInput) => Promise<ContactCategory>;
  onCreatePriority: (data: ContactPriorityInput) => Promise<ContactPriority>;
  onToggleFavorite?: (contact: ContactDetail) => void;
  defaultTemplate?: DefaultTemplate;
  suggestedTags?: string[];
}) {
  const [saving, setSaving] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [creatingCategory, setCreatingCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryColor, setNewCategoryColor] = useState('#6366f1');
  const [savingCategory, setSavingCategory] = useState(false);
  const [creatingPriority, setCreatingPriority] = useState(false);
  const [newPriorityName, setNewPriorityName] = useState('');
  const [newPriorityColor, setNewPriorityColor] = useState('#6b7280');
  const [savingPriority, setSavingPriority] = useState(false);
  const [middleName, setMiddleName] = useState('');
  const [namePrefix, setNamePrefix] = useState('');
  const [nameSuffix, setNameSuffix] = useState('');
  const [nickname, setNickname] = useState('');
  const [emails, setEmails] = useState<LabeledValue[]>([]);
  const [phones, setPhones] = useState<LabeledValue[]>([]);
  const [company, setCompany] = useState('');
  const [title, setTitle] = useState('');
  const [department, setDepartment] = useState('');
  const [notes, setNotes] = useState('');
  const [source, setSource] = useState('');
  const [categoryId, setCategoryId] = useState<string>('none');
  const [priorityId, setPriorityId] = useState<string>('none');
  const [gatheredAt, setGatheredAt] = useState('');
  const [tagInput, setTagInput] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [tagSuggestionsOpen, setTagSuggestionsOpen] = useState(false);
  const [tagHighlightIdx, setTagHighlightIdx] = useState(-1);
  const [addresses, setAddresses] = useState<AddressValue[]>([]);
  const [birthday, setBirthday] = useState('');
  const [anniversary, setAnniversary] = useState('');
  const [websites, setWebsites] = useState<LabeledValue[]>([]);
  const [isFavorite, setIsFavorite] = useState(false);
  const [showAdditional, setShowAdditional] = useState(false);
  const [historyEntries, setHistoryEntries] = useState<HistoryEntry[]>([]);

  // Custom fields
  const [customFields, setCustomFields] = useState<CustomFieldDefinition[]>([]);
  const [customFieldValues, setCustomFieldValues] = useState<Record<string, unknown>>({});
  const [manageFieldsOpen, setManageFieldsOpen] = useState(false);

  // Load custom field definitions
  useEffect(() => {
    if (open) {
      api
        .listCustomFields()
        .then((res) => setCustomFields(res.fields))
        .catch(() => {});
    }
  }, [open]);

  const fetchCustomFields = useCallback(() => {
    api
      .listCustomFields()
      .then((res) => setCustomFields(res.fields))
      .catch(() => {});
  }, []);

  // Snapshot of initial values for change detection / revert
  const [initialSnapshot, setInitialSnapshot] = useState<FormSnapshot | null>(null);

  const hasChanges = useMemo(() => {
    if (!initialSnapshot || !contact) return false;
    if (
      firstName !== initialSnapshot.firstName ||
      lastName !== initialSnapshot.lastName ||
      middleName !== initialSnapshot.middleName ||
      namePrefix !== initialSnapshot.namePrefix ||
      nameSuffix !== initialSnapshot.nameSuffix ||
      nickname !== initialSnapshot.nickname ||
      company !== initialSnapshot.company ||
      title !== initialSnapshot.title ||
      department !== initialSnapshot.department ||
      notes !== initialSnapshot.notes ||
      source !== initialSnapshot.source ||
      categoryId !== initialSnapshot.categoryId ||
      priorityId !== initialSnapshot.priorityId ||
      gatheredAt !== initialSnapshot.gatheredAt ||
      birthday !== initialSnapshot.birthday ||
      anniversary !== initialSnapshot.anniversary ||
      isFavorite !== initialSnapshot.isFavorite
    )
      return true;
    // Deep compare arrays of labeled values
    const labeledEqual = (a: LabeledValue[], b: LabeledValue[]) =>
      a.length === b.length && a.every((v, i) => v.value === b[i].value && v.label === b[i].label);
    if (!labeledEqual(emails, initialSnapshot.emails)) return true;
    if (!labeledEqual(phones, initialSnapshot.phones)) return true;
    if (!labeledEqual(websites, initialSnapshot.websites)) return true;
    // Addresses
    if (JSON.stringify(addresses) !== JSON.stringify(initialSnapshot.addresses)) return true;
    // Tags
    if (
      tags.length !== initialSnapshot.tags.length ||
      tags.some((t, i) => t !== initialSnapshot.tags[i])
    )
      return true;
    // History entries
    if (
      historyEntries.length !== initialSnapshot.historyEntries.length ||
      historyEntries.some(
        (h, i) =>
          h.text !== initialSnapshot.historyEntries[i].text ||
          h.date !== initialSnapshot.historyEntries[i].date ||
          h.showDate !== initialSnapshot.historyEntries[i].showDate,
      )
    )
      return true;
    return false;
  }, [
    initialSnapshot,
    contact,
    firstName,
    lastName,
    middleName,
    namePrefix,
    nameSuffix,
    nickname,
    emails,
    phones,
    company,
    title,
    department,
    notes,
    source,
    categoryId,
    priorityId,
    gatheredAt,
    tags,
    addresses,
    birthday,
    anniversary,
    isFavorite,
    websites,
    historyEntries,
  ]);

  const applySnapshot = useCallback((snap: FormSnapshot) => {
    setFirstName(snap.firstName);
    setLastName(snap.lastName);
    setMiddleName(snap.middleName);
    setNamePrefix(snap.namePrefix);
    setNameSuffix(snap.nameSuffix);
    setNickname(snap.nickname);
    setEmails(snap.emails);
    setPhones(snap.phones);
    setCompany(snap.company);
    setTitle(snap.title);
    setDepartment(snap.department);
    setNotes(snap.notes);
    setSource(snap.source);
    setCategoryId(snap.categoryId);
    setPriorityId(snap.priorityId);
    setGatheredAt(snap.gatheredAt);
    setTags(snap.tags);
    setAddresses(snap.addresses);
    setBirthday(snap.birthday);
    setAnniversary(snap.anniversary);
    setWebsites(snap.websites);
    setHistoryEntries(snap.historyEntries);
    setIsFavorite(snap.isFavorite);
  }, []);

  function buildInitialSnapshot(c: ContactDetail | null): FormSnapshot {
    const storedHistory = c?.metadata?.contactHistory;
    let entries: HistoryEntry[];
    if (Array.isArray(storedHistory)) {
      entries = storedHistory as HistoryEntry[];
    } else {
      const legacy = c?.metadata?.interactionsLog as string | undefined;
      entries = legacy ? [{ id: crypto.randomUUID(), text: legacy, date: c!.createdAt }] : [];
    }
    // Apply default template for new contacts only
    const tpl = !c ? defaultTemplate : undefined;
    return {
      firstName: c?.firstName || '',
      lastName: c?.lastName || '',
      middleName: c?.middleName || '',
      namePrefix: c?.namePrefix || '',
      nameSuffix: c?.nameSuffix || '',
      nickname: c?.nickname || '',
      emails: c?.emails || [],
      phones: c?.phones || [],
      company: c?.company || '',
      title: c?.title || '',
      department: c?.department || '',
      notes: c?.notes || '',
      source: c?.source || '',
      categoryId: c?.categoryId || tpl?.categoryId || 'none',
      priorityId: c?.priorityId || tpl?.priorityId || 'none',
      gatheredAt: c?.gatheredAt
        ? new Date(c.gatheredAt).toISOString().slice(0, 10)
        : new Date().toISOString().slice(0, 10),
      tags: c?.tags || tpl?.tags || [],
      addresses: c?.addresses || [],
      birthday: c?.birthday || '',
      anniversary: c?.anniversary || '',
      websites: c?.websites || [],
      historyEntries: entries,
      isFavorite: c?.isFavorite || false,
    };
  }

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      const snap = buildInitialSnapshot(contact);
      applySnapshot(snap);
      setInitialSnapshot(snap);
      setTagInput('');
      // Load custom field values from metadata
      const cfValues = (contact?.metadata?.customFields as Record<string, unknown>) || {};
      setCustomFieldValues(cfValues);
      // Auto-expand additional details if any extra fields have data
      setShowAdditional(
        !!(
          snap.middleName ||
          snap.namePrefix ||
          snap.nameSuffix ||
          snap.nickname ||
          snap.department ||
          snap.birthday ||
          snap.anniversary ||
          snap.websites.length > 0 ||
          snap.addresses.length > 0
        ),
      );
    }
  }, [open, contact, applySnapshot]);

  const filteredTagSuggestions = useMemo(() => {
    if (!tagInput.trim()) return [];
    const q = tagInput.trim().toLowerCase();
    return suggestedTags
      .filter((t) => t.toLowerCase().includes(q) && !tags.includes(t.toLowerCase()))
      .slice(0, 8);
  }, [tagInput, suggestedTags, tags]);

  const addTag = useCallback(
    (raw: string) => {
      const newTag = raw.trim().toLowerCase();
      if (newTag && !tags.includes(newTag)) {
        setTags([...tags, newTag]);
      }
      setTagInput('');
      setTagSuggestionsOpen(false);
      setTagHighlightIdx(-1);
    },
    [tags],
  );

  const handleTagKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'ArrowDown' && filteredTagSuggestions.length > 0) {
        e.preventDefault();
        setTagSuggestionsOpen(true);
        setTagHighlightIdx((prev) => (prev < filteredTagSuggestions.length - 1 ? prev + 1 : 0));
        return;
      }
      if (e.key === 'ArrowUp' && filteredTagSuggestions.length > 0) {
        e.preventDefault();
        setTagHighlightIdx((prev) => (prev > 0 ? prev - 1 : filteredTagSuggestions.length - 1));
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        if (tagHighlightIdx >= 0 && tagHighlightIdx < filteredTagSuggestions.length) {
          addTag(filteredTagSuggestions[tagHighlightIdx]);
        } else if (tagInput.trim()) {
          addTag(tagInput);
        }
        return;
      }
      if (e.key === 'Escape') {
        setTagSuggestionsOpen(false);
        setTagHighlightIdx(-1);
      }
    },
    [tagInput, tags, filteredTagSuggestions, tagHighlightIdx, addTag],
  );

  const removeTag = useCallback(
    (tag: string) => {
      setTags(tags.filter((t) => t !== tag));
    },
    [tags],
  );

  const buildSaveData = useCallback(
    (): ContactInput => ({
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      middleName: middleName.trim(),
      namePrefix: namePrefix.trim(),
      nameSuffix: nameSuffix.trim(),
      nickname: nickname.trim(),
      emails: emails
        .filter((e) => e.value.trim())
        .map((e) => ({ value: e.value.trim(), label: e.label.trim() })),
      phones: phones
        .filter((p) => p.value.trim())
        .map((p) => ({ value: p.value.trim(), label: p.label.trim() })),
      company: company.trim() || null,
      title: title.trim() || null,
      department: department.trim(),
      notes: notes.trim() || null,
      source: source.trim() || null,
      categoryId: categoryId === 'none' ? null : categoryId,
      priorityId: priorityId === 'none' ? null : priorityId,
      // Date inputs return YYYY-MM-DD; the API requires an ISO date-time string.
      gatheredAt: gatheredAt ? new Date(`${gatheredAt}T00:00:00Z`).toISOString() : null,
      tags,
      addresses: addresses.filter(
        (a) => a.street || a.city || a.state || a.postalCode || a.country,
      ),
      birthday: birthday || null,
      anniversary: anniversary || null,
      websites: websites
        .filter((w) => w.value.trim())
        .map((w) => ({ value: w.value.trim(), label: w.label.trim() })),
      metadata: {
        contactHistory: historyEntries.length > 0 ? historyEntries : null,
        customFields: Object.keys(customFieldValues).length > 0 ? customFieldValues : undefined,
      },
      isFavorite,
    }),
    [
      firstName,
      lastName,
      middleName,
      namePrefix,
      nameSuffix,
      nickname,
      emails,
      phones,
      company,
      title,
      department,
      notes,
      source,
      categoryId,
      priorityId,
      gatheredAt,
      tags,
      addresses,
      birthday,
      anniversary,
      websites,
      historyEntries,
      isFavorite,
      customFieldValues,
    ],
  );

  // Auto-save when closing an existing contact
  const handleClose = useCallback(async () => {
    if (contact && hasChanges && (firstName.trim() || lastName.trim())) {
      setSaving(true);
      try {
        await onSave(buildSaveData(), contact.id);
      } catch {
        // Error handled by parent
      } finally {
        setSaving(false);
      }
    }
    onOpenChange(false);
  }, [contact, hasChanges, firstName, lastName, buildSaveData, onSave, onOpenChange]);

  // For new contacts, use the form submit flow
  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!firstName.trim() && !lastName.trim()) {
        toast({ title: 'First or last name is required', variant: 'destructive' });
        return;
      }

      setSaving(true);
      try {
        await onSave(buildSaveData(), contact?.id);
        onOpenChange(false);
      } catch {
        // Error handled by parent
      } finally {
        setSaving(false);
      }
    },
    [firstName, lastName, contact, buildSaveData, onSave, onOpenChange],
  );

  const handleRevert = useCallback(() => {
    if (initialSnapshot) {
      applySnapshot(initialSnapshot);
    }
  }, [initialSnapshot, applySnapshot]);

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(isOpen) => {
          if (!isOpen) handleClose();
        }}
      >
        <DialogContent
          className={`${contact ? 'max-w-4xl pt-12' : 'max-w-3xl'} max-h-[90vh] flex flex-col`}
          onOpenAutoFocus={contact ? (e) => e.preventDefault() : undefined}
          hideClose={!!contact}
        >
          <DialogHeader>
            {contact ? (
              <>
                <button
                  type="button"
                  onClick={handleClose}
                  className="absolute right-4 top-4 rounded-sm opacity-70 hover:opacity-100 transition-opacity"
                >
                  <ChevronLeft className="h-7 w-7" />
                  <span className="sr-only">Close</span>
                </button>
                <div className="border-b pb-3">
                  <div className="flex items-center gap-3 mb-3">
                    <ContactAvatar contact={contact} size="lg" />
                    {onToggleFavorite && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className={`h-8 w-8 ${contact.isFavorite ? 'text-yellow-500' : 'text-muted-foreground'}`}
                        onClick={() => onToggleFavorite(contact)}
                        title={contact.isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                      >
                        <Star className={`h-5 w-5 ${contact.isFavorite ? 'fill-current' : ''}`} />
                      </Button>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Input
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      placeholder="First name"
                      className="text-lg font-semibold border-muted flex-1"
                    />
                    <Input
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      placeholder="Last name"
                      className="text-lg font-semibold border-muted flex-1"
                    />
                  </div>
                </div>
                <DialogDescription>Update contact information.</DialogDescription>
              </>
            ) : (
              <>
                <DialogTitle>Add Contact</DialogTitle>
                <DialogDescription>Add a new contact to your book.</DialogDescription>
              </>
            )}
          </DialogHeader>

          <DialogBody>
            <form onSubmit={handleSubmit}>
              <div className={contact ? 'flex gap-6' : ''}>
                {/* Left side: contact fields */}
                <div className={`space-y-4 ${contact ? 'flex-1 min-w-0' : ''}`}>
                  {!contact && (
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="contact-first-name">First Name *</Label>
                        <Input
                          id="contact-first-name"
                          value={firstName}
                          onChange={(e) => setFirstName(e.target.value)}
                          placeholder="First name"
                          required
                          autoFocus
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="contact-last-name">Last Name</Label>
                        <Input
                          id="contact-last-name"
                          value={lastName}
                          onChange={(e) => setLastName(e.target.value)}
                          placeholder="Last name"
                        />
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-6">
                    <LabeledEntryList
                      entries={emails}
                      onChange={setEmails}
                      labels={EMAIL_LABELS}
                      defaultLabel="Work"
                      placeholder="email@example.com"
                      fieldLabel="Emails"
                      inputType="email"
                    />
                    <LabeledEntryList
                      entries={phones}
                      onChange={setPhones}
                      labels={PHONE_LABELS}
                      defaultLabel="Mobile"
                      placeholder="+1 (555) 123-4567"
                      fieldLabel="Phones"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="contact-company">Company</Label>
                      <Input
                        id="contact-company"
                        value={company}
                        onChange={(e) => setCompany(e.target.value)}
                        placeholder="Company name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="contact-title">Title</Label>
                      <Input
                        id="contact-title"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder="Job title"
                      />
                    </div>
                  </div>

                  {/* Additional Details (collapsible) */}
                  <div>
                    <button
                      type="button"
                      onClick={() => setShowAdditional(!showAdditional)}
                      className="text-sm text-primary hover:underline"
                    >
                      {showAdditional ? 'Hide' : 'Show'} additional details
                    </button>
                    {showAdditional && (
                      <div className="space-y-4 mt-3 pl-0">
                        {/* Name details row */}
                        <div className="grid grid-cols-5 gap-2">
                          <div className="space-y-1">
                            <Label htmlFor="contact-prefix" className="text-xs">
                              Prefix
                            </Label>
                            <Input
                              id="contact-prefix"
                              value={namePrefix}
                              onChange={(e) => setNamePrefix(e.target.value)}
                              placeholder="Mr."
                              className="h-8 text-sm"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label htmlFor="contact-middle" className="text-xs">
                              Middle
                            </Label>
                            <Input
                              id="contact-middle"
                              value={middleName}
                              onChange={(e) => setMiddleName(e.target.value)}
                              placeholder="Middle"
                              className="h-8 text-sm"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label htmlFor="contact-suffix" className="text-xs">
                              Suffix
                            </Label>
                            <Input
                              id="contact-suffix"
                              value={nameSuffix}
                              onChange={(e) => setNameSuffix(e.target.value)}
                              placeholder="Jr."
                              className="h-8 text-sm"
                            />
                          </div>
                          <div className="col-span-2 space-y-1">
                            <Label htmlFor="contact-nickname" className="text-xs">
                              Nickname
                            </Label>
                            <Input
                              id="contact-nickname"
                              value={nickname}
                              onChange={(e) => setNickname(e.target.value)}
                              placeholder="Nickname"
                              className="h-8 text-sm"
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-3 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="contact-department">Department</Label>
                            <Input
                              id="contact-department"
                              value={department}
                              onChange={(e) => setDepartment(e.target.value)}
                              placeholder="Department"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="contact-birthday">Birthday</Label>
                            <Input
                              id="contact-birthday"
                              type="date"
                              value={birthday}
                              onChange={(e) => setBirthday(e.target.value)}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="contact-anniversary">Anniversary</Label>
                            <Input
                              id="contact-anniversary"
                              type="date"
                              value={anniversary}
                              onChange={(e) => setAnniversary(e.target.value)}
                            />
                          </div>
                        </div>

                        <LabeledEntryList
                          entries={websites}
                          onChange={setWebsites}
                          labels={WEBSITE_LABELS}
                          defaultLabel="Work"
                          placeholder="https://example.com"
                          fieldLabel="Websites"
                          inputType="url"
                        />

                        {/* Addresses */}
                        <div className="space-y-2">
                          <Label>Addresses</Label>
                          {addresses.map((addr, idx) => (
                            <div key={idx} className="rounded-md border p-3 space-y-2">
                              <div className="flex items-center justify-between">
                                <select
                                  value={addr.label}
                                  onChange={(e) => {
                                    const updated = [...addresses];
                                    updated[idx] = { ...addr, label: e.target.value };
                                    setAddresses(updated);
                                  }}
                                  className="text-sm border rounded px-2 py-1 bg-background"
                                >
                                  {ADDRESS_LABELS.map((l) => (
                                    <option key={l} value={l}>
                                      {l}
                                    </option>
                                  ))}
                                </select>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7"
                                  onClick={() =>
                                    setAddresses(addresses.filter((_, i) => i !== idx))
                                  }
                                >
                                  <X className="h-3.5 w-3.5" />
                                </Button>
                              </div>
                              <Input
                                value={addr.street}
                                onChange={(e) => {
                                  const updated = [...addresses];
                                  updated[idx] = { ...addr, street: e.target.value };
                                  setAddresses(updated);
                                }}
                                placeholder="Street"
                                className="h-8 text-sm"
                              />
                              <div className="grid grid-cols-3 gap-2">
                                <Input
                                  value={addr.city}
                                  onChange={(e) => {
                                    const updated = [...addresses];
                                    updated[idx] = { ...addr, city: e.target.value };
                                    setAddresses(updated);
                                  }}
                                  placeholder="City"
                                  className="h-8 text-sm"
                                />
                                <Input
                                  value={addr.state}
                                  onChange={(e) => {
                                    const updated = [...addresses];
                                    updated[idx] = { ...addr, state: e.target.value };
                                    setAddresses(updated);
                                  }}
                                  placeholder="State"
                                  className="h-8 text-sm"
                                />
                                <Input
                                  value={addr.postalCode}
                                  onChange={(e) => {
                                    const updated = [...addresses];
                                    updated[idx] = { ...addr, postalCode: e.target.value };
                                    setAddresses(updated);
                                  }}
                                  placeholder="Postal code"
                                  className="h-8 text-sm"
                                />
                              </div>
                              <Input
                                value={addr.country}
                                onChange={(e) => {
                                  const updated = [...addresses];
                                  updated[idx] = { ...addr, country: e.target.value };
                                  setAddresses(updated);
                                }}
                                placeholder="Country"
                                className="h-8 text-sm"
                              />
                            </div>
                          ))}
                          {addresses.length < 5 && (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="w-full"
                              onClick={() =>
                                setAddresses([
                                  ...addresses,
                                  {
                                    label: 'Home',
                                    street: '',
                                    city: '',
                                    state: '',
                                    postalCode: '',
                                    country: '',
                                  },
                                ])
                              }
                            >
                              <Plus className="h-3.5 w-3.5 mr-1" />
                              Add Address
                            </Button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="contact-category">Category</Label>
                      {creatingCategory ? (
                        <div className="space-y-2 rounded-md border p-2">
                          <Input
                            value={newCategoryName}
                            onChange={(e) => setNewCategoryName(e.target.value)}
                            placeholder="Category name"
                            autoFocus
                          />
                          <div className="flex flex-wrap gap-1.5">
                            {PRESET_COLORS.map((c) => (
                              <button
                                key={c}
                                type="button"
                                onClick={() => setNewCategoryColor(c)}
                                className={`h-5 w-5 rounded-full border-2 transition-all ${
                                  newCategoryColor === c
                                    ? 'border-foreground scale-110'
                                    : 'border-transparent'
                                }`}
                                style={{ backgroundColor: c }}
                              />
                            ))}
                          </div>
                          <div className="flex gap-1.5">
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              className="flex-1"
                              onClick={() => {
                                setCreatingCategory(false);
                                setNewCategoryName('');
                              }}
                            >
                              Cancel
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              className="flex-1"
                              disabled={!newCategoryName.trim() || savingCategory}
                              onClick={async () => {
                                setSavingCategory(true);
                                try {
                                  const created = await onCreateCategory({
                                    name: newCategoryName.trim(),
                                    color: newCategoryColor,
                                  });
                                  setCategoryId(created.id);
                                  setCreatingCategory(false);
                                  setNewCategoryName('');
                                } catch {
                                  // Error handled by parent
                                } finally {
                                  setSavingCategory(false);
                                }
                              }}
                            >
                              {savingCategory ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                'Create'
                              )}
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <Select
                          value={categoryId}
                          onValueChange={(val) => {
                            if (val === '__new__') {
                              setCreatingCategory(true);
                            } else {
                              setCategoryId(val);
                            }
                          }}
                        >
                          <SelectTrigger id="contact-category">
                            <SelectValue placeholder="Select category" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Uncategorized</SelectItem>
                            {categories.map((cat) => (
                              <SelectItem key={cat.id} value={cat.id}>
                                <span className="flex items-center gap-2">
                                  <span
                                    className="h-2 w-2 rounded-full inline-block"
                                    style={{ backgroundColor: cat.color }}
                                  />
                                  {cat.name}
                                </span>
                              </SelectItem>
                            ))}
                            <SelectItem value="__new__">
                              <span className="flex items-center gap-2 text-primary">
                                <Plus className="h-3 w-3" />
                                New Category
                              </span>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="contact-source">Source</Label>
                      <Input
                        id="contact-source"
                        value={source}
                        onChange={(e) => setSource(e.target.value)}
                        placeholder="e.g., Referral, Conference"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="contact-priority">Priority</Label>
                      {creatingPriority ? (
                        <div className="space-y-2 rounded-md border p-2">
                          <Input
                            value={newPriorityName}
                            onChange={(e) => setNewPriorityName(e.target.value)}
                            placeholder="Priority name"
                            autoFocus
                          />
                          <div className="flex flex-wrap gap-1.5">
                            {PRESET_COLORS.map((c) => (
                              <button
                                key={c}
                                type="button"
                                onClick={() => setNewPriorityColor(c)}
                                className={`h-5 w-5 rounded-full border-2 transition-all ${
                                  newPriorityColor === c
                                    ? 'border-foreground scale-110'
                                    : 'border-transparent'
                                }`}
                                style={{ backgroundColor: c }}
                              />
                            ))}
                          </div>
                          <div className="flex gap-1.5">
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              className="flex-1"
                              onClick={() => {
                                setCreatingPriority(false);
                                setNewPriorityName('');
                              }}
                            >
                              Cancel
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              className="flex-1"
                              disabled={!newPriorityName.trim() || savingPriority}
                              onClick={async () => {
                                setSavingPriority(true);
                                try {
                                  const created = await onCreatePriority({
                                    name: newPriorityName.trim(),
                                    color: newPriorityColor,
                                  });
                                  setPriorityId(created.id);
                                  setCreatingPriority(false);
                                  setNewPriorityName('');
                                } catch {
                                  // Error handled by parent
                                } finally {
                                  setSavingPriority(false);
                                }
                              }}
                            >
                              {savingPriority ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                'Create'
                              )}
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <Select
                          value={priorityId}
                          onValueChange={(val) => {
                            if (val === '__new__') {
                              setCreatingPriority(true);
                            } else {
                              setPriorityId(val);
                            }
                          }}
                        >
                          <SelectTrigger id="contact-priority">
                            <SelectValue placeholder="Select priority" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">No Priority</SelectItem>
                            {priorities.map((p) => (
                              <SelectItem key={p.id} value={p.id}>
                                <span className="flex items-center gap-2">
                                  <span
                                    className="h-2.5 w-2.5 rounded-full inline-block"
                                    style={{ backgroundColor: p.color }}
                                  />
                                  {p.name}
                                </span>
                              </SelectItem>
                            ))}
                            <SelectItem value="__new__">
                              <span className="flex items-center gap-2 text-primary">
                                <Plus className="h-3 w-3" />
                                New Priority
                              </span>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="contact-gathered">Date Gathered</Label>
                      <Input
                        id="contact-gathered"
                        type="date"
                        value={gatheredAt}
                        onChange={(e) => setGatheredAt(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="contact-tags">Tags</Label>
                    <div className="flex flex-wrap gap-1 mb-1">
                      {tags.map((tag) => (
                        <Badge key={tag} variant="secondary" className="text-xs">
                          {tag}
                          <button
                            type="button"
                            onClick={() => removeTag(tag)}
                            className="ml-1 hover:text-destructive"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                    <div className="relative">
                      <Input
                        id="contact-tags"
                        value={tagInput}
                        onChange={(e) => {
                          setTagInput(e.target.value);
                          setTagSuggestionsOpen(true);
                          setTagHighlightIdx(-1);
                        }}
                        onKeyDown={handleTagKeyDown}
                        onFocus={() => tagInput.trim() && setTagSuggestionsOpen(true)}
                        onBlur={() => {
                          // Delay to allow click on suggestion
                          setTimeout(() => setTagSuggestionsOpen(false), 150);
                        }}
                        placeholder="Type a tag and press Enter"
                        autoComplete="off"
                      />
                      {tagSuggestionsOpen && filteredTagSuggestions.length > 0 && (
                        <div className="absolute z-50 w-full mt-1 rounded-md border bg-popover shadow-md max-h-40 overflow-y-auto">
                          {filteredTagSuggestions.map((suggestion, idx) => (
                            <button
                              key={suggestion}
                              type="button"
                              className={`w-full text-left px-3 py-1.5 text-sm hover:bg-accent ${
                                idx === tagHighlightIdx ? 'bg-accent' : ''
                              }`}
                              onMouseDown={(e) => {
                                e.preventDefault();
                                addTag(suggestion);
                              }}
                            >
                              {suggestion}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="contact-notes">Notes</Label>
                    <Textarea
                      id="contact-notes"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Notes about this contact..."
                      rows={3}
                    />
                  </div>

                  {/* Attachments (edit mode only) */}
                  {contact && <ContactAttachments contactId={contact.id} />}

                  {/* Custom Fields */}
                  {customFields.length > 0 && (
                    <div className="border-t pt-4">
                      <CustomFieldInputs
                        fields={customFields}
                        values={customFieldValues}
                        onChange={(fieldName, value) => {
                          setCustomFieldValues((prev) => {
                            const next = { ...prev };
                            if (value === null || value === '' || value === undefined) {
                              delete next[fieldName];
                            } else {
                              next[fieldName] = value;
                            }
                            return next;
                          });
                        }}
                        onManageFields={() => setManageFieldsOpen(true)}
                      />
                    </div>
                  )}
                  {customFields.length === 0 && (
                    <div className="border-t pt-4">
                      <CustomFieldInputs
                        fields={[]}
                        values={{}}
                        onChange={() => {}}
                        onManageFields={() => setManageFieldsOpen(true)}
                      />
                    </div>
                  )}
                </div>

                {/* Right side: Interactions, Relationships & Cross-Arche (edit mode only) */}
                {contact && (
                  <>
                    <div className="w-px bg-border flex-shrink-0" />
                    <div className="flex-1 min-w-0 flex flex-col gap-4">
                      <InteractionsTimeline contactId={contact.id} />
                      <RelationshipsSection contactId={contact.id} />
                    </div>
                  </>
                )}
              </div>

              <DialogFooter className="mt-4">
                {contact ? (
                  <>
                    {hasChanges && (
                      <Button type="button" variant="outline" onClick={handleRevert}>
                        <RotateCcw className="h-4 w-4 mr-2" />
                        Revert Changes
                      </Button>
                    )}
                    {saving && (
                      <span className="flex items-center text-sm text-muted-foreground">
                        <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                        Saving...
                      </span>
                    )}
                  </>
                ) : (
                  <>
                    <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" disabled={saving}>
                      {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                      Add Contact
                    </Button>
                  </>
                )}
              </DialogFooter>
            </form>
          </DialogBody>
        </DialogContent>
      </Dialog>

      <ManageCustomFieldsDialog
        open={manageFieldsOpen}
        onOpenChange={setManageFieldsOpen}
        fields={customFields}
        onFieldsChanged={fetchCustomFields}
      />
    </>
  );
}
